// app/(otherPages)/reset_password/page.jsx
'use client'

import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import Link from 'next/link'
import { toast } from 'react-toastify'

const API_REQUEST_OTP = '/api/member/register/register_lineRequestOtp'
const API_VERIFY_OTP = '/api/member/register/register_lineVerifyOtp'
const API_UPDATE_PWD = '/api/member/update_PasswordWithOTP'

const Card = ({ children }) => (
  <div
    className="mx-auto reset-card"
    style={{
      maxWidth: 640,
      background: '#fff',
      borderRadius: 12,
      boxShadow: '0 8px 24px rgba(0,0,0,.08)',
      padding: 24,
    }}
  >
    {children}
    <style jsx global>{`
      .reset-card .form-floating > label {
        pointer-events: none;
      }
    `}</style>
  </div>
)
const SectionTitle = ({ children }) => (
  <h2 className="section-title text-center fs-3 mb-4">{children}</h2>
)

export default function ResetPassword() {
  const storeNumber = useMemo(
    () => Number(process.env.NEXT_PUBLIC_STORE_NUMBER || 1),
    []
  )

  const [step, setStep] = useState('phone') // 'phone' | 'otp' | 'password' | 'success'
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // 手機（受控）
  const [mobile, setMobile] = useState('')
  const mobileRef = useRef(null)

  // OTP（不受控）
  const otpRef = useRef(null)
  const [sessionId, setSessionId] = useState(null)

  // ✅ 已驗證成功的 OTP 會被存在這裡（不要再去讀已卸載的 input）
  const verifiedOtpRef = useRef('')

  // 倒數（用 ref，不觸發重繪）
  const resendBtnRef = useRef(null)
  const cdTimerRef = useRef(null)
  const cdLeftRef = useRef(0)
  const [resendDisabled, setResendDisabled] = useState(false)

  // 新密碼（不受控）
  const pwd1Ref = useRef(null)
  const pwd2Ref = useRef(null)

  const postJson = useCallback(async (url, body, method = 'POST') => {
    const r = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      cache: 'no-store',
      body: JSON.stringify(body || {}),
    })
    const t = await r.text()
    let data = {}
    try {
      data = JSON.parse(t)
    } catch {
      data = {}
    }
    if (!r.ok || data?.Error)
      throw new Error(data?.Error || data?.error || `HTTP ${r.status}`)
    return data
  }, [])
  const onlyDigits = (s) => String(s || '').replace(/[^\d]/g, '')

  const stopCooldown = () => {
    if (cdTimerRef.current) clearInterval(cdTimerRef.current)
    cdTimerRef.current = null
    cdLeftRef.current = 0
    if (resendBtnRef.current) resendBtnRef.current.textContent = '重新發送'
    setResendDisabled(false)
  }
  const startCooldown = (sec = 60) => {
    stopCooldown()
    cdLeftRef.current = sec
    if (resendBtnRef.current)
      resendBtnRef.current.textContent = `重新發送 (${cdLeftRef.current})`
    setResendDisabled(true)
    cdTimerRef.current = setInterval(() => {
      cdLeftRef.current -= 1
      if (cdLeftRef.current <= 0) return stopCooldown()
      if (resendBtnRef.current)
        resendBtnRef.current.textContent = `重新發送 (${cdLeftRef.current})`
    }, 1000)
  }
  useEffect(() => () => stopCooldown(), [])

  useEffect(() => {
    if (step === 'phone') mobileRef.current?.focus()
    if (step === 'otp') otpRef.current?.focus()
    if (step === 'password') pwd1Ref.current?.focus()
  }, [step])

  const handleSendOtp = async () => {
    setError('')
    const m = onlyDigits(mobile)
    if (!m || m.length < 8) {
      setError('請輸入正確的手機號碼')
      return
    }
    setLoading(true)
    try {
      const data = await postJson(API_REQUEST_OTP, {
        StoreNumber: storeNumber,
        Channel: 'sms',
        Mobile: m,
      })
      setSessionId(data?.SessionID || null)
      // 清掉之前可能保存的已驗證 OTP
      verifiedOtpRef.current = ''
      setStep('otp')
      startCooldown(60)
      toast.success('驗證碼已發送')
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  const handleResend = async () => {
    if (resendDisabled || loading) return
    await handleSendOtp()
  }

  const handleVerifyOtp = async () => {
    setError('')
    const code = String(otpRef.current?.value || '').trim()
    if (!code) {
      setError('請輸入驗證碼')
      return
    }
    if (!sessionId) {
      setError('請重新發送驗證碼')
      return
    }

    setLoading(true)
    try {
      await postJson(API_VERIFY_OTP, {
        StoreNumber: storeNumber,
        SessionID: sessionId,
        Code: code,
      })
      // ✅ 驗證通過後把 OTP 存起來
      verifiedOtpRef.current = code
      toast.success('驗證成功')
      setStep('password')
      stopCooldown()
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  const handleUpdatePassword = async () => {
    setError('')
    const newPwd = String(pwd1Ref.current?.value || '')
    const cfmPwd = String(pwd2Ref.current?.value || '')
    const m = onlyDigits(mobile)
    const otp = String(verifiedOtpRef.current || '').trim() // ✅ 用已保存的 OTP

    if (!newPwd || newPwd.length < 8) {
      setError('新密碼至少 8 碼')
      return
    }
    if (newPwd !== cfmPwd) {
      setError('兩次密碼不一致')
      return
    }
    if (!m) {
      setError('手機號碼有誤')
      return
    }
    if (!otp) {
      setError('請先完成手機驗證')
      setStep('otp')
      return
    }

    setLoading(true)
    try {
      await postJson(
        API_UPDATE_PWD,
        {
          StoreNumber: storeNumber,
          Mobile: m,
          OTP: otp,
          NewPassword: newPwd,
          ConfirmPassword: cfmPwd,
        },
        'POST'
      )
      toast.success('密碼已更新，請使用新密碼登入')
      setStep('success')
      stopCooldown()
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <section
      className="login-register container"
      style={{ paddingTop: 24, paddingBottom: 48 }}
    >
      <SectionTitle>重設密碼</SectionTitle>

      <div className="text-center mb-3" style={{ fontWeight: 600 }}>
        <span style={{ color: step === 'phone' ? '#111' : '#999' }}>
          1. 輸入手機
        </span>
        <span className="mx-2">›</span>
        <span style={{ color: step === 'otp' ? '#111' : '#999' }}>
          2. 驗證碼
        </span>
        <span className="mx-2">›</span>
        <span
          style={{
            color: step === 'password' || step === 'success' ? '#111' : '#999',
          }}
        >
          3. 設定新密碼
        </span>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      {step === 'phone' && (
        <Card>
          <div className="form-floating mb-3">
            <input
              ref={mobileRef}
              id="mobile"
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              className="form-control form-control_gray"
              placeholder=" "
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              disabled={loading}
            />
            <label htmlFor="mobile">手機號碼 *</label>
          </div>

          <button
            className="btn btn-primary w-100 text-uppercase"
            type="button"
            disabled={loading}
            onClick={handleSendOtp}
          >
            {loading ? '處理中…' : '發送驗證碼'}
          </button>

          <div className="customer-option mt-4 text-center">
            <span className="text-secondary">返回</span>{' '}
            <Link href="/LoginOrRegister" className="btn-text">
              登入
            </Link>
          </div>
        </Card>
      )}

      {step === 'otp' && (
        <Card>
          <label htmlFor="otp" className="form-label fw-semibold">
            簡訊驗證碼 *
          </label>
          <input
            ref={otpRef}
            id="otp"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            className="form-control form-control_gray mb-3"
            defaultValue=""
            onInput={(e) => {
              e.currentTarget.value = onlyDigits(e.currentTarget.value)
            }}
            disabled={loading}
          />

          <div className="d-flex gap-2">
            <button
              ref={resendBtnRef}
              className="btn btn-outline-secondary w-50"
              type="button"
              disabled={resendDisabled || loading}
              onClick={handleResend}
            >
              重新發送
            </button>
            <button
              className="btn btn-primary w-50 text-uppercase"
              type="button"
              disabled={loading}
              onClick={handleVerifyOtp}
            >
              {loading ? '檢查中…' : '下一步'}
            </button>
          </div>

          <div className="customer-option mt-4 text-center">
            <button
              type="button"
              className="btn-text"
              onClick={() => {
                stopCooldown()
                setStep('phone')
              }}
              disabled={loading}
            >
              上一步（修改手機）
            </button>
          </div>
        </Card>
      )}

      {step === 'password' && (
        <Card>
          <div className="form-floating mb-3">
            <input
              ref={pwd1Ref}
              id="newPwd"
              type="password"
              className="form-control form-control_gray"
              placeholder=" "
              defaultValue=""
              disabled={loading}
            />
            <label htmlFor="newPwd">新密碼（至少 8 碼）*</label>
          </div>

          <div className="form-floating mb-4">
            <input
              ref={pwd2Ref}
              id="confirmPwd"
              type="password"
              className="form-control form-control_gray"
              placeholder=" "
              defaultValue=""
              disabled={loading}
            />
            <label htmlFor="confirmPwd">再次輸入新密碼 *</label>
          </div>

          <button
            className="btn btn-primary w-100 text-uppercase"
            type="button"
            disabled={loading}
            onClick={handleUpdatePassword}
          >
            {loading ? '更新中…' : '更新密碼'}
          </button>

          <div className="customer-option mt-4 text-center">
            <button
              type="button"
              className="btn-text"
              onClick={() => setStep('otp')}
              disabled={loading}
            >
              上一步（回驗證碼）
            </button>
          </div>
        </Card>
      )}

      {step === 'success' && (
        <Card>
          <div className="text-center py-4">
            <h4 className="mb-3">密碼已更新</h4>
            <p className="text-secondary mb-4">請使用新密碼重新登入。</p>
            <Link href="/LoginOrRegister" className="btn btn-primary">
              前往登入
            </Link>
          </div>
        </Card>
      )}
    </section>
  )
}
